import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree, FormField } from "@angular/forms/signals";
import { AccountType, Brand } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import { AccountSelectionService, SbStrategyService } from "@front/m19-services";
import {
  IErrorComponent,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  ISelectMenuImports,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  AlgoModeConfig,
  AlgoModeSelectionComponent,
} from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { of, switchMap } from "rxjs";

export interface SbFormSetupModel {
  name: string;
  brand: Brand | null;
  algo: Partial<AlgoModeConfig> | null;
}

@Component({
  selector: "app-sb-form-setup",
  imports: [
    TranslocoDirective,
    FormField,
    IFormFieldComponent,
    IInputDirective,
    ILabelComponent,
    IErrorComponent,
    ISelectMenuImports,
    AlgoModeSelectionComponent,
  ],
  host: {
    class: "block",
  },
  templateUrl: "./sb-form-setup.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbFormSetupComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly sbStrategyService = inject(SbStrategyService);

  readonly setup = input.required<FieldTree<SbFormSetupModel>>();
  readonly hideName = input(false);
  readonly hideAlgo = input(false);

  readonly searchQuery = signal("");

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);
  readonly currencyCode = computed(() => Marketplaces[this.accountMarketplace().marketplace].currency);

  readonly brands = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        am.accountType === AccountType.VENDOR
          ? of([] as Brand[])
          : this.sbStrategyService.getBrands(am.accountId, am.marketplace),
      ),
    ),
    { initialValue: [] as Brand[] },
  );

  readonly filteredBrands = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const brands = this.brands();
    if (!query) return brands;
    return brands.filter((brand) => (brand.brandName ?? "").toLowerCase().includes(query));
  });

  constructor() {
    effect(() => {
      if (this.isVendor()) return;
      const brands = this.brands();
      if (brands.length === 0) return;
      untracked(() => {
        if (!this.setup().brand().value()) {
          this.selectBrand(brands[0]!);
        }
      });
    });
  }

  selectBrand(brand: Brand | null | undefined) {
    if (!brand || this.isVendor()) return;
    if (brand.brandEntityId === this.setup().brand().value()?.brandEntityId) return;

    this.setup().brand().markAsDirty();
    this.setup().brand().value.set(brand);
  }

  selectAlgoModeConfig(algoModeConfig: Partial<AlgoModeConfig>) {
    this.setup().algo().markAsDirty();
    this.setup().algo().value.set(algoModeConfig);
  }
}
